import { useEffect, useState, type FormEvent } from "react";
import { api, type BillingView, type Me, type Plan, type PlanSummary } from "../api";
import { Checkbox, Field, FormError } from "../components/Form";
import { Loading } from "../components/Layout";
import { config } from "../config";
import { errorMessage } from "../messages";
import { useSession } from "../session";
import { formatPrice } from "../validation";
import { upgradeEstimate } from "../billing";

// Paid plans, through Cardcom: choosing a plan, the return from Cardcom's
// page, and managing the subscription. The server decides everything; this
// page only shows its state and sends the customer's requests.

const dateFormat = new Intl.DateTimeFormat("he-IL", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jerusalem" });
const formatDate = (iso: string) => dateFormat.format(new Date(iso));
/** A month ends at midnight; the last day it covers is the day before. */
const lastDayOf = (periodEnd: string) => dateFormat.format(new Date(new Date(periodEnd).getTime() - 1));

type Subscription = NonNullable<BillingView["subscription"]>;

export function BillingSection({ me, view, onChange, reload }: { me: Me; view: BillingView; onChange: (view: BillingView) => void; reload: () => Promise<void> }) {
  const [returnedFrom, setReturnedFrom] = useState(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    return checkout ? { checkoutId: checkout, failed: params.get("failed") === "1" } : null;
  });
  const [changing, setChanging] = useState(false);
  // The message about the return from Cardcom is about that payment only;
  // anything the customer does next replaces it.
  const changed = (next: BillingView) => {
    setReturnedFrom(null);
    onChange(next);
  };

  return (
    <>
      {returnedFrom && <CheckoutReturn {...returnedFrom} onSettled={reload} />}
      {view.subscription ? (
        <>
          <SubscriptionCard view={view} subscription={view.subscription} onChange={changed} onChangePlan={() => setChanging(true)} />
          {changing && view.subscription.status === "active" && (
            <PlanChooser
              me={me}
              subscription={view.subscription}
              onDone={(next) => {
                changed(next);
                setChanging(false);
              }}
              onClose={() => setChanging(false)}
            />
          )}
          {view.payments.length > 0 && <PaymentsCard payments={view.payments} />}
        </>
      ) : (
        <PlanChooser me={me} subscription={null} onDone={changed} />
      )}
    </>
  );
}

/** Back from Cardcom's page: the payment counts once the server has confirmed it with Cardcom. */
function CheckoutReturn({ checkoutId, failed, onSettled }: { checkoutId: string; failed: boolean; onSettled: () => Promise<void> }) {
  const { token } = useSession();
  const [state, setState] = useState<"waiting" | "paid" | "failed" | "slow">(failed ? "failed" : "waiting");

  useEffect(() => {
    history.replaceState(null, "", location.pathname);
    if (failed) {
      return;
    }
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; attempt < 30 && !cancelled; attempt++) {
        try {
          const { status } = await api.checkoutStatus(await token(), checkoutId);
          if (status !== "open") {
            if (!cancelled) {
              setState(status === "paid" ? "paid" : "failed");
              await onSettled();
            }
            return;
          }
        } catch {
          // Keep waiting: the confirmation may still be on its way.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (!cancelled) {
        setState("slow");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checkoutId, failed, onSettled, token]);

  if (state === "waiting") {
    return (
      <p className="alert alert-info" role="status">
        <span className="spinner spinner-small" aria-hidden="true" /> מאשרים את התשלום מול חברת הסליקה…
      </p>
    );
  }
  if (state === "paid") {
    return <p className="alert alert-success" role="status">התשלום התקבל. החבילה פעילה, והחשבונית נשלחה לאימייל שלכם.</p>;
  }
  if (state === "slow") {
    return (
      <p className="alert alert-info" role="status">
        התשלום עדיין בבדיקה. זה לוקח לפעמים כמה דקות; רעננו את הדף בעוד רגע.
      </p>
    );
  }
  return <p className="alert alert-error" role="alert">התשלום לא הושלם, ולא חויבתם. אפשר לבחור חבילה ולנסות שוב.</p>;
}

function StatusBadge({ status }: { status: Subscription["status"] }) {
  const labels: Record<Subscription["status"], [string, string]> = {
    active: ["פעיל", "badge badge-ok"],
    past_due: ["התשלום נכשל", "badge badge-error"],
    cancelled: ["מבוטל", "badge"],
    ended: ["הסתיים", "badge"],
  };
  const [label, className] = labels[status];
  return <span className={className}>{label}</span>;
}

function SubscriptionCard({
  view,
  subscription,
  onChange,
  onChangePlan,
}: {
  view: BillingView;
  subscription: Subscription;
  onChange: (view: BillingView) => void;
  onChangePlan: () => void;
}) {
  const { token } = useSession();
  const [busy, setBusy] = useState<"cancel" | "resume" | "card" | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { plan, nextPlan, status, card } = subscription;

  const run = async (action: "cancel" | "resume" | "card") => {
    setBusy(action);
    setError(null);
    try {
      if (action === "card") {
        const page = await api.updateCard(await token());
        location.assign(page.url);
        return;
      }
      onChange(action === "cancel" ? await api.cancelSubscription(await token()) : await api.resumeSubscription(await token()));
      setConfirmCancel(false);
    } catch (failure) {
      setError(errorMessage(failure));
    }
    setBusy(null);
  };

  return (
    <section className="card card-highlight">
      <div className="card-title-row">
        <h2>חבילה {plan.name}</h2>
        <StatusBadge status={status} />
      </div>
      <p className="big-number">
        {view.credits.total} <span>{view.credits.total === 1 ? "פרק זמין לעיבוד" : "פרקים זמינים לעיבוד"}</span>
      </p>
      <dl className="details">
        <dt>מחיר</dt>
        <dd>{formatPrice(plan.priceAgorot)} לחודש, כולל מע"מ</dd>
        {subscription.renews && (
          <>
            <dt>החיוב הבא</dt>
            <dd>
              {formatDate(subscription.currentPeriodEnd)}
              {nextPlan && ` · עוברים לחבילה ${nextPlan.name}, ${formatPrice(nextPlan.priceAgorot)} לחודש`}
            </dd>
          </>
        )}
        {status === "cancelled" && (
          <>
            <dt>פעיל עד</dt>
            <dd>{lastDayOf(subscription.currentPeriodEnd)} (כולל). לא יהיו חיובים נוספים.</dd>
          </>
        )}
        {card.last4 && (
          <>
            <dt>כרטיס</dt>
            <dd dir="ltr">
              •••• {card.last4}
              {card.expiry && ` · ${card.expiry.slice(0, 2)}/${card.expiry.slice(2)}`}
            </dd>
          </>
        )}
      </dl>
      {status === "past_due" && (
        <p className="alert alert-error">
          החיוב החודשי האחרון לא עבר. עדכנו כרטיס, וחברת הסליקה תנסה לחייב שוב. הפרקים של החודש החדש ייכנסו מיד כשהחיוב יעבור.
        </p>
      )}
      {!subscription.recurringReady && status !== "cancelled" && (
        <p className="alert alert-info">מסיימים להגדיר את החיוב החודשי. אין צורך לעשות דבר.</p>
      )}
      <FormError message={error} />
      {confirmCancel ? (
        <div className="confirm">
          <p>
            <strong>לבטל את המנוי?</strong> לא יבוצעו חיובים נוספים, והחבילה תישאר פעילה עד {lastDayOf(subscription.currentPeriodEnd)}{" "}
            (כולל). אפשר לחדש עד אז בלחיצה.
          </p>
          <div className="actions">
            <button type="button" className="button button-danger" onClick={() => void run("cancel")} disabled={busy !== null}>
              {busy === "cancel" && <span className="spinner spinner-small" aria-hidden="true" />}
              כן, לבטל את המנוי
            </button>
            <button type="button" className="link-button" onClick={() => setConfirmCancel(false)}>
              השארת המנוי
            </button>
          </div>
        </div>
      ) : (
        <div className="actions">
          {status === "active" && (
            <button type="button" className="button button-primary" onClick={onChangePlan}>
              שינוי חבילה
            </button>
          )}
          {(status === "active" || status === "past_due") && (
            <>
              <button type="button" className="button button-secondary" onClick={() => void run("card")} disabled={busy !== null}>
                {busy === "card" && <span className="spinner spinner-small" aria-hidden="true" />}
                עדכון כרטיס
              </button>
              <button type="button" className="link-button" onClick={() => setConfirmCancel(true)}>
                ביטול מנוי
              </button>
            </>
          )}
          {status === "cancelled" && (
            <button type="button" className="button button-primary" onClick={() => void run("resume")} disabled={busy !== null}>
              {busy === "resume" && <span className="spinner spinner-small" aria-hidden="true" />}
              חידוש המנוי
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** Choosing a plan: a new subscription goes to Cardcom's page; a change is confirmed here. */
function PlanChooser({
  me,
  subscription,
  onDone,
  onClose,
}: {
  me: Me;
  subscription: Subscription | null;
  onDone: (view: BillingView) => void;
  onClose?: () => void;
}) {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [selected, setSelected] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.catalog().then(setPlans, (failure: unknown) => setError(errorMessage(failure)));
  }, []);

  const label = (plan: Plan): string => {
    if (!subscription) return "בחירה";
    if (plan.key === subscription.plan.key) return subscription.nextPlan ? "להישאר בחבילה הזאת" : "החבילה הנוכחית";
    if (plan.key === subscription.nextPlan?.key) return "תתחיל בחיוב הבא";
    return plan.priceAgorot > subscription.plan.priceAgorot ? "שדרוג עכשיו" : "מעבר בחיוב הבא";
  };
  const disabled = (plan: Plan) =>
    Boolean(subscription && ((plan.key === subscription.plan.key && !subscription.nextPlan) || plan.key === subscription.nextPlan?.key));

  return (
    <section className="card" id="plans">
      <div className="card-title-row">
        <h2>{subscription ? "שינוי חבילה" : "בחירת חבילה"}</h2>
        {onClose && (
          <button type="button" className="link-button" onClick={onClose}>
            סגירה
          </button>
        )}
      </div>
      <p className="subtitle">
        {subscription
          ? "שדרוג מתחיל מיד, ומחויב רק על יתרת החודש. מעבר לחבילה זולה יותר מתחיל בחיוב הבא."
          : "מנוי חודשי מתחדש. אפשר לבטל בכל עת באתר, והחבילה נשארת פעילה עד סוף החודש ששולם."}
      </p>
      {!plans && !error && <Loading />}
      {plans && (
        <div className="plans">
          {plans.map((plan) => (
            <article key={plan.key} className={plan.key === (selected?.key ?? subscription?.plan.key) ? "plan plan-current" : "plan"}>
              <h3>{plan.name}</h3>
              <p className="price">
                {formatPrice(plan.priceAgorot)}
                <span> לחודש</span>
              </p>
              <ul>
                <li>{plan.episodesPerPeriod} פרקים בחודש</li>
                <li>עד {plan.reelsPerEpisode} רילסים מכל פרק</li>
                <li>פרקים באורך עד {plan.maxEpisodeMinutes} דקות</li>
              </ul>
              <button
                type="button"
                className="button button-primary"
                onClick={() => {
                  setSelected(plan);
                  setError(null);
                }}
                disabled={disabled(plan)}
              >
                {label(plan)}
              </button>
            </article>
          ))}
        </div>
      )}
      <FormError message={error} />
      {selected && !subscription && <CheckoutForm me={me} plan={selected} onError={setError} />}
      {selected && subscription && !disabled(selected) && (
        <ChangeConfirm subscription={subscription} plan={selected} onDone={onDone} onError={setError} onCancel={() => setSelected(null)} />
      )}
      {!subscription && (
        <p className="fine-print">
          שאלות לפני שבוחרים?{" "}
          <a href={config.whatsappUrl} target="_blank" rel="noopener">
            כתבו לנו בוואטסאפ
          </a>
          .
        </p>
      )}
    </section>
  );
}

/** The invoice details and the customer's consent to a monthly charge, then Cardcom's page. */
function CheckoutForm({ me, plan, onError }: { me: Me; plan: Plan; onError: (message: string | null) => void }) {
  const { token } = useSession();
  const [invoiceName, setInvoiceName] = useState(me.fullName ?? "");
  const [taxId, setTaxId] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    if (!consent) {
      setConsentError("כדי להמשיך צריך לאשר את החיוב החודשי.");
      return;
    }
    setBusy(true);
    try {
      const page = await api.checkout(await token(), {
        planKey: plan.key,
        invoiceName: invoiceName.trim() || undefined,
        invoiceTaxId: taxId.trim() || undefined,
      });
      location.assign(page.url);
    } catch (failure) {
      onError(errorMessage(failure));
      setBusy(false);
    }
  };

  return (
    <form className="checkout-form" onSubmit={(event) => void submit(event)} noValidate>
      <h3>חבילה {plan.name}: {formatPrice(plan.priceAgorot)} לחודש</h3>
      <div className="grid-2">
        <Field label="שם לחשבונית" value={invoiceName} onChange={setInvoiceName} autoComplete="organization" hint="שם העסק או השם הפרטי." />
        <Field label="ח.פ. / ע.מ. (לא חובה)" value={taxId} onChange={setTaxId} dir="ltr" inputMode="numeric" hint="לעסקים שצריכים את המספר בחשבונית." />
      </div>
      <Checkbox
        checked={consent}
        onChange={(value) => {
          setConsent(value);
          setConsentError(undefined);
        }}
        error={consentError}
      >
        אני מאשר/ת חיוב חודשי מתחדש של {formatPrice(plan.priceAgorot)} בכרטיס האשראי עד לביטול. אפשר לבטל בכל עת באתר, והחבילה
        נשארת פעילה עד סוף החודש ששולם.
      </Checkbox>
      <button type="submit" className="button button-primary" disabled={busy}>
        {busy && <span className="spinner spinner-small" aria-hidden="true" />}
        המשך לתשלום מאובטח
      </button>
      <p className="fine-print">התשלום מתבצע בעמוד המאובטח של Cardcom. פרטי הכרטיס לא עוברים דרכנו.</p>
    </form>
  );
}

function ChangeConfirm({
  subscription,
  plan,
  onDone,
  onError,
  onCancel,
}: {
  subscription: Subscription;
  plan: Plan;
  onDone: (view: BillingView) => void;
  onError: (message: string | null) => void;
  onCancel: () => void;
}) {
  const { token } = useSession();
  const [busy, setBusy] = useState(false);
  const staying = plan.key === subscription.plan.key;
  const upgrade = !staying && plan.priceAgorot > subscription.plan.priceAgorot;
  const estimate = upgradeEstimate(subscription, plan);

  const confirm = async () => {
    setBusy(true);
    onError(null);
    try {
      onDone(await api.changePlan(await token(), plan.key));
    } catch (failure) {
      onError(errorMessage(failure));
      setBusy(false);
    }
  };

  return (
    <div className="confirm">
      <p>
        {staying
          ? `נשארים בחבילה ${plan.name}, והמחיר בחיוב הבא חוזר ל-${formatPrice(plan.priceAgorot)}.`
          : upgrade
            ? `השדרוג לחבילה ${plan.name} מתחיל עכשיו. נחייב ${formatPrice(estimate.agorot)} על יתרת החודש ותקבלו ${estimate.episodes} פרקים נוספים. מהחיוב הבא: ${formatPrice(plan.priceAgorot)} לחודש.`
            : `החבילה תתחלף ל-${plan.name} בחיוב הבא, ב-${formatDate(subscription.currentPeriodEnd)}, ב-${formatPrice(plan.priceAgorot)} לחודש. עד אז נשארים בחבילה הנוכחית.`}
      </p>
      <div className="actions">
        <button type="button" className="button button-primary" onClick={() => void confirm()} disabled={busy}>
          {busy && <span className="spinner spinner-small" aria-hidden="true" />}
          {upgrade ? "אישור השדרוג" : "אישור"}
        </button>
        <button type="button" className="link-button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </div>
  );
}

function PaymentsCard({ payments }: { payments: BillingView["payments"] }) {
  const kinds = { first: "חודש ראשון", renewal: "חיוב חודשי", upgrade: "שדרוג" } as const;
  return (
    <section className="card">
      <h2>תשלומים וחשבוניות</h2>
      <table className="payments">
        <thead>
          <tr>
            <th>תאריך</th>
            <th>פירוט</th>
            <th>סכום</th>
            <th>חשבונית</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={`${payment.date}-${payment.kind}`}>
              <td>{formatDate(payment.date)}</td>
              <td>
                {kinds[payment.kind]} · {payment.planName}
              </td>
              <td>{formatPrice(payment.amountAgorot)}</td>
              <td>
                {payment.documentUrl ? (
                  <a href={payment.documentUrl} target="_blank" rel="noopener">
                    {payment.documentNumber ? `מס' ${payment.documentNumber}` : "צפייה"}
                  </a>
                ) : (
                  (payment.documentNumber ?? "נשלחה באימייל")
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
